import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Check, X, Trash } from "lucide-react";
import { toast } from "sonner";
import { flagUrl, nationalityLabel } from "@/lib/nationalities";

/**
 * AdminCompetitionUsers — mirror of AdminTrainingUsers (Utilizadores VS TEMPO)
 * but exclusive to COMPETITION-status games played WITH credentials. Rows come
 * from /admin/competition-results (ranked credentials with a best_time); edits
 * and deletes operate on the credential itself.
 */
export default function AdminCompetitionUsers() {
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [sel, setSel] = useState("all"); // "all" | tierId (tiered) | "1".."5"
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [rankingCfg, setRankingCfg] = useState({ top_n: 10, subtitle_pt: "", subtitle_en: "", subtitle_es: "" });
  const [savingRankingCfg, setSavingRankingCfg] = useState(false);

  useEffect(() => {
    api.get("/games").then((r) => {
      const compGames = (r.data || []).filter((g) => g.status === "competition");
      setGames(compGames);
      if (compGames.length) setGameId(compGames[0].id);
    });
  }, []);

  useEffect(() => {
    if (!gameId) return;
    api.get("/results/settings").then((r) => {
      const cfg = (r.data || []).find((c) => c.game_id === gameId);
      if (cfg) setRankingCfg({
        top_n: cfg.top_n ?? 10,
        subtitle_pt: cfg.subtitle_pt || "",
        subtitle_en: cfg.subtitle_en || "",
        subtitle_es: cfg.subtitle_es || "",
        visible: cfg.visible !== false,
        show_name: cfg.show_name !== false,
        show_nationality: cfg.show_nationality !== false,
        show_school: cfg.show_school !== false,
        show_best_time: cfg.show_best_time !== false,
      });
    }).catch(() => {});
  }, [gameId]);

  const saveRankingCfg = async () => {
    if (!gameId) return;
    setSavingRankingCfg(true);
    try {
      await api.put(`/results/settings/${gameId}`, { game_id: gameId, ...rankingCfg });
      toast.success("Configuração de ranking guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar configuração");
    } finally {
      setSavingRankingCfg(false);
    }
  };

  const load = async () => {
    if (!gameId) { setRows([]); return; }
    try {
      let config = {};
      if (sel !== "all") {
        config = useTiers ? { params: { tier_id: sel } } : { params: { level: parseInt(sel, 10) } };
      }
      const { data } = await api.get(`/admin/competition-results/${gameId}`, config);
      setRows(data || []);
    } catch (e) {
      toast.error("Erro a carregar resultados");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gameId, sel]);

  const currentGame = useMemo(() => games.find((g) => g.id === gameId), [games, gameId]);
  const useTiers = currentGame?.level_system === "tiers" && Array.isArray(currentGame?.tiers) && currentGame.tiers.length > 0;
  const selectedTier = useTiers && sel !== "all" ? (currentGame.tiers || []).find((t) => t.id === sel) : null;
  const selLabel = sel === "all" ? "" : (useTiers ? (selectedTier?.name || "escalão") : `Nível ${sel}`);
  const tierName = (lvl) => {
    if (!useTiers) return `Nível ${lvl}`;
    const t = currentGame.tiers.find((x) => Number(x.level) === Number(lvl));
    return t?.name || `Nível ${lvl}`;
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (filter.trim()) {
      const q = filter.toLowerCase();
      out = out.filter((r) => (r.name || "").toLowerCase().includes(q));
    }
    const n = Number(rankingCfg.top_n) || 0;
    if (n > 0) out = out.slice(0, n);
    return out;
  }, [rows, filter, rankingCfg.top_n]);

  const startEdit = (r) => { setEditingId(r.id); setEditName(r.name || ""); };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await api.put(`/credentials/${editingId}`, { name: editName });
      toast.success("Nome atualizado");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Apagar este utilizador de competição (credencial e respetivos resultados)?")) return;
    await api.delete(`/credentials/${id}`);
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    toast.success("Apagado");
    load();
  };

  const toggleSelect = (id) => {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filtered.map((r) => r.id);
    const allSel = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    setSelected((s) => {
      const n = new Set(s);
      if (allSel) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Apagar ${selected.size} utilizador(es) selecionado(s)?`)) return;
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/credentials/bulk-delete", { ids });
      toast.success(`${data.deleted} apagado(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const removeAll = async () => {
    if (!gameId) return;
    try {
      const params = { game_id: gameId };
      if (sel !== "all") {
        if (useTiers) params.tier_id = sel; else params.level = parseInt(sel, 10);
      }
      const res = await api.delete("/credentials", { params });
      toast.success(`${res.data?.deleted || 0} apagados`);
      setConfirmDeleteAll(false);
      load();
    } catch (e) {
      toast.error("Erro ao apagar");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">Utilizadores COMPETIÇÃO</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Jogo (modo competição)</Label>
            <Select value={gameId} onValueChange={(v) => { setGameId(v); setSel("all"); }}>
              <SelectTrigger data-testid="cu-game"><SelectValue placeholder="Escolher jogo" /></SelectTrigger>
              <SelectContent>
                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{useTiers ? "Nível/Escalão" : "Nível"}</Label>
            <Select
              value={sel}
              onValueChange={setSel}
            >
              <SelectTrigger data-testid="cu-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="cu-level-all">{useTiers ? "Todos os escalões" : "Todos os níveis"}</SelectItem>
                {useTiers
                  ? currentGame.tiers.map((tr) => (
                      <SelectItem key={tr.id} value={tr.id} data-testid={`cu-tier-${tr.id}`}>
                        {tr.name} <span className="text-slate-400 text-xs">(Nv {tr.level})</span>
                      </SelectItem>
                    ))
                  : [1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`cu-level-${n}`}>Nível {n}</SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Filtrar por nome</Label>
            <Input
              data-testid="cu-filter"
              placeholder="Procurar nome..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              data-testid="cu-delete-selected"
              onClick={deleteSelected}
              disabled={!selected.size}
              variant="outline"
              className="border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Apagar selecionados ({selected.size})
            </Button>
            <Button
              data-testid="cu-delete-all"
              onClick={() => setConfirmDeleteAll(true)}
              disabled={!rows.length}
              variant="outline"
              className="border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              <Trash className="w-4 h-4 mr-2" /> Apagar{sel !== "all" ? ` (${selLabel})` : " TODOS"}
            </Button>
          </div>
        </div>
      </div>

      {confirmDeleteAll && (
        <div className="bg-rose-50 border border-rose-300 rounded-xl p-4 mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-rose-800">Tem a certeza? Vão ser apagadas as credenciais {sel !== "all" ? `do escalão ${selLabel}` : "de todos os escalões"} deste jogo e os respetivos resultados. Esta ação não pode ser desfeita.</div>
          <div className="flex gap-2">
            <Button data-testid="cu-confirm-delete-all" onClick={removeAll} className="bg-rose-600 text-white">Sim, apagar todos</Button>
            <Button onClick={() => setConfirmDeleteAll(false)} variant="outline">Cancelar</Button>
          </div>
        </div>
      )}

      {gameId && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="cu-ranking-cfg">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-amber-900 uppercase tracking-wide">🏆 Configuração do Ranking público (COMPETIÇÃO)</div>
            <Button size="sm" onClick={saveRankingCfg} disabled={savingRankingCfg} className="bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50" data-testid="cu-ranking-save">
              {savingRankingCfg ? "A guardar…" : "Guardar"}
            </Button>
          </div>
          <div className="grid md:grid-cols-4 gap-3 mb-3">
            <div>
              <Label className="text-xs">Nº de jogadores no top</Label>
              <Select value={String(rankingCfg.top_n ?? 10)} onValueChange={(v) => setRankingCfg((c) => ({ ...c, top_n: Number(v) }))}>
                <SelectTrigger data-testid="cu-topn"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="20">Top 20</SelectItem>
                  <SelectItem value="50">Top 50</SelectItem>
                  <SelectItem value="100">Top 100</SelectItem>
                  <SelectItem value="0">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {["pt","en","es"].map((code) => (
              <div key={code}>
                <Label className="text-xs">Texto por baixo do título · {code.toUpperCase()}</Label>
                <Input
                  value={rankingCfg[`subtitle_${code}`] || ""}
                  onChange={(e) => setRankingCfg((c) => ({ ...c, [`subtitle_${code}`]: e.target.value }))}
                  placeholder={code === "pt" ? "Ex.: Melhores tempos da Grande Final…" : ""}
                  data-testid={`cu-subtitle-${code}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-xs" data-testid="cu-results-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-2 py-2 w-6">
                <input
                  type="checkbox"
                  data-testid="cu-select-all"
                  className="w-4 h-4 cursor-pointer"
                  checked={filtered.length > 0 && filtered.every((r) => selected.has(r.id))}
                  onChange={toggleSelectAllVisible}
                />
              </th>
              <th className="text-left px-2 py-2 font-bold text-slate-700 uppercase text-[10px] tracking-wider w-14">#</th>
              <th className="text-left px-2 py-2 font-bold text-slate-700 uppercase text-[10px] tracking-wider">Nome</th>
              <th className="text-left px-2 py-2 font-bold text-slate-700 uppercase text-[10px] tracking-wider">Escola</th>
              <th className="text-left px-2 py-2 font-bold text-slate-700 uppercase text-[10px] tracking-wider w-24">País</th>
              <th className="text-left px-2 py-2 font-bold text-slate-700 uppercase text-[10px] tracking-wider w-28">{useTiers ? "Escalão" : "Nível"}</th>
              <th className="text-left px-2 py-2 font-bold text-slate-700 uppercase text-[10px] tracking-wider w-20">Tempo</th>
              <th className="text-left px-2 py-2 font-bold text-slate-700 uppercase text-[10px] tracking-wider w-16"></th>
            </tr>
          </thead>
          <tbody data-testid="cu-table">
            {filtered.map((r, i) => {
              const isEditing = editingId === r.id;
              const isSel = selected.has(r.id);
              return (
                <tr key={r.id || i} className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-rose-50" : ""}`}>
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      data-testid={`cu-select-${r.id}`}
                      className="w-4 h-4 cursor-pointer"
                      checked={isSel}
                      onChange={() => toggleSelect(r.id)}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">
                    <span className={`inline-flex items-center justify-center min-w-[24px] h-5 rounded-full text-[10px] font-bold ${
                      r.ranking === 1 ? "bg-amber-100 text-amber-700 ring-1 ring-amber-400" :
                      r.ranking === 2 ? "bg-slate-200 text-slate-700" :
                      r.ranking === 3 ? "bg-orange-100 text-orange-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>{r.ranking ?? i + 1}</span>
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">
                    {isEditing ? (
                      <Input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                        data-testid={`cu-edit-input-${r.id}`}
                        className="h-7 text-xs"
                      />
                    ) : (
                      <span className="truncate block max-w-[220px]" title={r.name || "—"}>{r.name || "—"}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 truncate max-w-[180px]" title={r.school || ""} data-testid={`cu-row-school-${r.id}`}>{r.school || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-700">
                    <span className="inline-flex items-center gap-1">
                      {flagUrl(r.nationality) && <img src={flagUrl(r.nationality)} alt={r.nationality} className="w-4 h-3 object-cover rounded-sm" />}
                      <span className="truncate max-w-[60px]" title={nationalityLabel(r.nationality) || r.nationality || "-"}>{nationalityLabel(r.nationality) || r.nationality || "-"}</span>
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">
                    <span className="inline-flex items-center justify-center h-5 px-2 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 ring-1 ring-amber-300" data-testid={`cu-row-level-${r.id}`}>
                      {useTiers
                        ? (() => {
                            const tr = (r.tier_id && currentGame.tiers.find((x) => x.id === r.tier_id))
                              || currentGame.tiers.find((x) => Number(x.level) === Number(r.level));
                            return tr?.name || tierName(r.level ?? 1);
                          })()
                        : tierName(r.level ?? 1)}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-slate-700 text-[11px]">{r.best_time != null ? `${r.best_time.toFixed(1)}s` : "-"}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} className="text-emerald-600 hover:text-emerald-700 p-1" data-testid={`cu-save-${r.id}`}><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingId(null)} className="text-slate-500 hover:text-slate-700 p-1"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(r)} className="text-sky-600 hover:text-sky-700 p-1" data-testid={`cu-edit-${r.id}`} title="Editar nome"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => removeOne(r.id)} className="text-rose-600 hover:text-rose-700 p-1" data-testid={`cu-delete-${r.id}`} title="Apagar"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400 text-xs">
                  {gameId ? "Sem resultados de competição para este jogo." : "Escolhe um jogo de competição para ver os resultados."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
